<template>
  <NPopover
    ref="popoverRef"
    :animated="false"
    class="handlebars-helper-tree-select"
    :on-clickoutside="handleClickOutside"
    placement="bottom"
    :show="showPopover"
    :show-arrow="false"
    trigger="manual"
    :width="300"
  >
    <template #trigger>
      <CButton
        class="handlebars-helper-tree-select__trigger"
        :themeOverrides="themeOverrides?.[selectedOverride]"
        @click="handleTriggerClick"
      >
        <CIcon class="mr-1" icon="code" size="16px" />
        <CIcon class="ml-1" icon="chevron-down" size="12px" />
      </CButton>
    </template>
    <NTreeSelect
      ref="treeSelectRef"
      block-line
      filterable
      :on-update:value="onSelect"
      :options="options"
      :override-default-node-click-behavior="override"
      :placeholder="t('canvasUI.CHandlebarsHelperTreeSelect.searchPlaceholder')"
    />
  </NPopover>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick } from 'vue'
import { NTreeSelect, NPopover, type TreeSelectOption, type TreeSelectOverrideNodeClickBehavior } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

const { t } = useI18n()

defineProps<{
  themeOverrides?: Record<string, any>
}>()

const emit = defineEmits<{ select: [value: string] }>()

const popoverRef = ref<InstanceType<typeof NPopover> | null>(null)
const treeSelectRef = ref<InstanceType<typeof NTreeSelect> | null>(null)
const selectedOverride = ref<'default' | 'active'>('default')
const showPopover = ref(false)
const override: TreeSelectOverrideNodeClickBehavior = ({ option }) => {
  if (option.children) {
    return 'toggleExpand'
  }
  return 'default'
}

// Handlebars helpers data organized by category
const helpers = [
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Comparison'),
    items: [
      {
        name: 'eq',
        template: '{{#if (eq value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.eq'),
      },
      {
        name: 'ne',
        template: '{{#if (ne value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.ne'),
      },
      {
        name: 'lt',
        template: '{{#if (lt value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.lt'),
      },
      {
        name: 'gt',
        template: '{{#if (gt value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.gt'),
      },
      {
        name: 'lte',
        template: '{{#if (lte value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.lte'),
      },
      {
        name: 'gte',
        template: '{{#if (gte value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.gte'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Logical'),
    items: [
      {
        name: 'and',
        template: '{{#if (and condition1 condition2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.and'),
      },
      {
        name: 'or',
        template: '{{#if (or condition1 condition2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.or'),
      },
      {
        name: 'not',
        template: '{{#if (not condition)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.not'),
      },
      {
        name: 'xor',
        template: '{{#if (xor condition1 condition2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.xor'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.String'),
    items: [
      {
        name: 'lowercase',
        template: '{{lowercase string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.lowercase'),
      },
      {
        name: 'uppercase',
        template: '{{uppercase string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.uppercase'),
      },
      {
        name: 'capitalize',
        template: '{{capitalize string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.capitalize'),
      },
      {
        name: 'titleCase',
        template: '{{titleCase string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.titleCase'),
      },
      {
        name: 'trim',
        template: '{{trim string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.trim'),
      },
      {
        name: 'substring',
        template: '{{substring string start end}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.substring'),
      },
      {
        name: 'replace',
        template: '{{replace string pattern replacement}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.replace'),
      },
      {
        name: 'concat',
        template: '{{concat string1 string2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.concat'),
      },
      {
        name: 'contains',
        template: '{{#if (contains string substring)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.contains'),
      },
      {
        name: 'startsWith',
        template: '{{#if (startsWith string prefix)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.startsWith'),
      },
      {
        name: 'endsWith',
        template: '{{#if (endsWith string suffix)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.endsWith'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Math'),
    items: [
      {
        name: 'add',
        template: '{{add number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.add'),
      },
      {
        name: 'subtract',
        template: '{{subtract number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.subtract'),
      },
      {
        name: 'multiply',
        template: '{{multiply number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.multiply'),
      },
      {
        name: 'divide',
        template: '{{divide number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.divide'),
      },
      {
        name: 'mod',
        template: '{{mod number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.mod'),
      },
      {
        name: 'floor',
        template: '{{floor number}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.floor'),
      },
      {
        name: 'ceil',
        template: '{{ceil number}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.ceil'),
      },
      {
        name: 'round',
        template: '{{round number}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.round'),
      },
      {
        name: 'toFixed',
        template: '{{toFixed number digits}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.toFixed'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Array'),
    items: [
      {
        name: 'first',
        template: '{{first array}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.first'),
      },
      {
        name: 'last',
        template: '{{last array}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.last'),
      },
      {
        name: 'nth',
        template: '{{nth array index}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.nth'),
      },
      {
        name: 'length',
        template: '{{length array}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.length'),
      },
      {
        name: 'join',
        template: '{{join array separator}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.join'),
      },
      {
        name: 'limitArray',
        template: '{{#each (limitArray array 3)}}{{this}}{{/each}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.limitArray'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Object'),
    items: [
      {
        name: 'keys',
        template: '{{#each (keys object)}}{{this}}{{/each}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.keys'),
      },
      {
        name: 'values',
        template: '{{#each (values object)}}{{this}}{{/each}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.values'),
      },
      {
        name: 'has',
        template: '{{#if (has object key)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.has'),
      },
      {
        name: 'get',
        template: '{{get object "path.to.property"}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.get'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Conditional'),
    items: [
      {
        name: 'if_eq',
        template: '{{#if (if_eq value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.if_eq'),
      },
      {
        name: 'if_ne',
        template: '{{#if (if_ne value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.if_ne'),
      },
      {
        name: 'if_lt',
        template: '{{#if (if_lt value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.if_lt'),
      },
      {
        name: 'if_gt',
        template: '{{#if (if_gt value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.if_gt'),
      },
      {
        name: 'if_lte',
        template: '{{#if (if_lte value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.if_lte'),
      },
      {
        name: 'if_gte',
        template: '{{#if (if_gte value1 value2)}}...{{/if}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.if_gte'),
      },
      {
        name: 'default',
        template: '{{default value defaultValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.default'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Date'),
    items: [
      {
        name: 'formatDate',
        template: '{{formatDate dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.formatDate'),
      },
      {
        name: 'formatDateLong',
        template: '{{formatDateLong dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.formatDateLong'),
      },
      { name: 'now', template: '{{now}}', description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.now') },
      {
        name: 'year',
        template: '{{year dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.year'),
      },
      {
        name: 'month',
        template: '{{month dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.month'),
      },
      {
        name: 'day',
        template: '{{day dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.day'),
      },
      {
        name: 'addDays',
        template: '{{addDays dateValue 7}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.addDays'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Format'),
    items: [
      {
        name: 'json',
        template: '{{json object}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.json'),
      },
      {
        name: 'numberFormat',
        template: '{{numberFormat number}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.numberFormat'),
      },
      {
        name: 'currency',
        template: '{{currency number "USD"}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.currency'),
      },
      {
        name: 'percentage',
        template: '{{percentage number 2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.percentage'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Encoding'),
    items: [
      {
        name: 'encodeURI',
        template: '{{encodeURI string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.encodeURI'),
      },
      {
        name: 'decodeURI',
        template: '{{decodeURI string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.decodeURI'),
      },
      {
        name: 'escapeHTML',
        template: '{{escapeHTML string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.escapeHTML'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.CRM'),
    items: [
      {
        name: 'phoneFormat',
        template: '{{phoneFormat phone}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.phoneFormat'),
      },
      {
        name: 'emailLink',
        template: '{{{emailLink email}}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.emailLink'),
      },
      {
        name: 'phoneLink',
        template: '{{{phoneLink phone}}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.phoneLink'),
      },
      {
        name: 'initials',
        template: '{{initials name}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.initials'),
      },
      {
        name: 'pluralize',
        template: '{{pluralize count "item" "items"}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.pluralize'),
      },
      {
        name: 'fileSize',
        template: '{{fileSize bytes}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.fileSize'),
      },
      {
        name: 'truncate',
        template: '{{truncate text 50}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.truncate'),
      },
      {
        name: 'slug',
        template: '{{slug title}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.slug'),
      },
      {
        name: 'emailDomain',
        template: '{{emailDomain email}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.emailDomain'),
      },
      {
        name: 'mapLink',
        template: '{{{mapLink address}}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.mapLink'),
      },
    ],
  },
]

// Function to transform helpers into TreeSelectOption structure
function transformHelpersToOptions(): TreeSelectOption[] {
  return helpers.map((category) => ({
    label: category.category,
    key: `category-${category.category}`,
    children: category.items.map((item) => ({
      label: item.name,
      key: `${category.category}-${item.name}`,
      description: item.description,
      template: item.template,
    })),
  }))
}

const options = computed(() => transformHelpersToOptions())

function onSelect(selection: string) {
  popoverRef.value?.setShow(false)
  showPopover.value = false
  selectedOverride.value = 'default'

  // Find the selected helper
  const [categoryName, helperName] = selection.split('-')
  const category = helpers.find((cat) => cat.category === categoryName)
  if (category) {
    const helper = category.items.find((item) => item.name === helperName)
    if (helper) {
      emit('select', helper.template)
    }
  }
}

async function handleShow(show: boolean) {
  if (show) {
    await nextTick()

    const element: HTMLElement | null = treeSelectRef.value?.$el.querySelector(
      '.handlebars-helper-tree-select.n-popover .n-base-selection',
    )
    element?.click()
  }
}

function handleTriggerClick() {
  showPopover.value = !showPopover.value
  handleShow(showPopover.value)
  selectedOverride.value = 'active'
}

function handleClickOutside() {
  // forcing this event to be executed after handleTriggerClick
  setTimeout(() => {
    showPopover.value = false
    selectedOverride.value = 'default'
  }, 0)
}
</script>

<style lang="scss" scoped>
.handlebars-helper-tree-select {
  :deep(.n-tree-select-menu .n-base-select-option) {
    display: flex;
    flex-direction: column;

    .n-base-select-option__content {
      display: flex;
      align-items: center;
    }
  }

  :deep(.n-tree-select-menu .n-base-select-option-body__prefix) {
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }
}

.handlebars-helper-tree-select__trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
</style>
